import React, { useState, useEffect } from 'react';
import { Empty, Progress } from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  PercentageOutlined,
  FolderOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';

/**
 * 环形图组件
 * 使用 SVG 绘制环形进度图
 */
const RingChart = ({ percent, size = 120, strokeWidth = 10, color, bgColor = '#e8e8e8' }) => {
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedPercent / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPercent(percent);
    }, 100);
    return () => clearTimeout(timer);
  }, [percent]);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* 背景圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth={strokeWidth}
        />
        {/* 进度圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{
            transition: 'stroke-dashoffset 1s ease-out'
          }}
        />
      </svg>
      {/* 中心文字 */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ color }}
      >
        <span className="text-xl font-bold">{percent}%</span>
      </div>
    </div>
  );
};

/**
 * 柱状图组件
 * 展示字段准确率的柱状图
 */
const BarChart = ({ data, getColor }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const maxValue = 100;

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-end gap-3 min-w-max pb-8 pt-4 px-2" style={{ minHeight: '200px' }}>
        {data.map(({ name, value }, index) => {
          const color = getColor(value);
          const height = animated ? (value / maxValue) * 150 : 0;

          return (
            <div
              key={name}
              className="flex flex-col items-center group"
              style={{ minWidth: '60px' }}
            >
              {/* 数值标签 - 在柱子上方显示百分比 */}
              <div
                className="text-xs font-medium mb-1 transition-all duration-700"
                style={{
                  color: color,
                  opacity: animated ? 1 : 0,
                  transform: animated ? 'translateY(0)' : 'translateY(10px)'
                }}
              >
                {value}%
              </div>
              {/* 柱子 */}
              <div
                className="w-10 rounded-t-lg transition-all duration-700 ease-out group-hover:opacity-80"
                style={{
                  height: `${height}px`,
                  background: `linear-gradient(180deg, ${color} 0%, ${color}99 100%)`,
                  boxShadow: `0 4px 12px ${color}40`
                }}
              />
              {/* 字段名 */}
              <div
                className="mt-2 text-xs text-gray-600 text-center max-w-[80px] truncate"
                title={name}
              >
                {name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * 结果统计展示组件
 * 用于展示要素抽取场景的准确率统计数据
 */
function ResultStatsDisplay({ data, title }) {
  const [expandedTypes, setExpandedTypes] = useState({});

  if (!data || Object.keys(data).length === 0) {
    return (
      <Empty
        description="暂无统计数据"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // 解析百分比字符串为数值
  const parsePercentage = (percentStr) => {
    if (!percentStr || typeof percentStr !== 'string') return 0;
    return parseFloat(percentStr.replace('%', '')) || 0;
  };

  // 根据准确率获取颜色（使用纯色，不使用渐变）
  const getColorByRate = (rate) => {
    if (rate >= 90) return '#52c41a'; // 绿色
    if (rate >= 75) return '#1890FF'; // 蓝色（纯色）
    if (rate >= 60) return '#faad14'; // 黄色
    return '#ff4d4f'; // 红色
  };

  // 获取颜色（使用纯色，不使用渐变）
  const getGradientByRate = (rate) => {
    if (rate >= 90) return '#52c41a'; // 绿色
    if (rate >= 75) return '#1890FF'; // 蓝色（纯色）
    if (rate >= 60) return '#faad14'; // 黄色
    return '#ff4d4f'; // 红色
  };

  // 获取状态标签
  const getStatusLabel = (rate) => {
    if (rate >= 90) return { text: '优秀', bg: 'bg-green-100', color: 'text-green-700' };
    if (rate >= 75) return { text: '良好', bg: 'bg-blue-100', color: 'text-blue-700' };
    if (rate >= 60) return { text: '一般', bg: 'bg-yellow-100', color: 'text-yellow-700' };
    return { text: '待提升', bg: 'bg-red-100', color: 'text-red-700' };
  };

  // 切换展开状态
  const toggleExpand = (typeName) => {
    setExpandedTypes(prev => ({
      ...prev,
      [typeName]: !prev[typeName]
    }));
  };

  // 提取整体指标
  const overallFileTypeRate = data['整体文件类型准确率'];
  const overallElementRate = data['整体要素准确率'];
  const fileTypeRateValue = parsePercentage(overallFileTypeRate);
  const elementRateValue = parsePercentage(overallElementRate);

  // 提取文件类型数据（排除整体指标）
  const fileTypes = Object.entries(data).filter(([key]) =>
    key !== '整体文件类型准确率' && key !== '整体要素准确率'
  );

  // 渲染整体指标卡片（带环形图）- 使用白色底色和纯色
  const renderOverallMetrics = () => {
    const fileTypeStatus = getStatusLabel(fileTypeRateValue);
    const elementStatus = getStatusLabel(elementRateValue);
    const blueColor = '#1890FF'; // 纯蓝色
    const greenColor = '#52c41a'; // 纯绿色

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* 整体文件类型准确率 - 使用白色底色 */}
        <div
          className="rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          style={{
            background: '#FFFFFF',
            border: '1px solid #e8e8e8'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ background: '#1890FF' }}
                >
                  <FileTextOutlined style={{ fontSize: '28px', color: '#fff' }} />
                </div>
                <div>
                  <div className="text-xl font-medium text-gray-800">整体文件类型准确率</div>
                  <span className={`text-sm px-3 py-1 rounded-full ${fileTypeStatus.bg} ${fileTypeStatus.color}`}>
                    {fileTypeStatus.text}
                  </span>
                </div>
              </div>
              <div className="mt-6">
                <Progress
                  percent={fileTypeRateValue}
                  showInfo={false}
                  strokeColor="#1890FF"
                  trailColor="#e5e7eb"
                  strokeWidth={10}
                  style={{ marginBottom: 0 }}
                />
              </div>
            </div>
            <div className="ml-8">
              <RingChart
                percent={fileTypeRateValue}
                size={140}
                strokeWidth={12}
                color={blueColor}
                bgColor="#e5e7eb"
              />
            </div>
          </div>
        </div>

        {/* 整体要素准确率 - 使用白色底色 */}
        <div
          className="rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          style={{
            background: '#FFFFFF',
            border: '1px solid #e8e8e8'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ background: '#52c41a' }}
                >
                  <PercentageOutlined style={{ fontSize: '28px', color: '#fff' }} />
                </div>
                <div>
                  <div className="text-xl font-medium text-gray-800">整体要素准确率</div>
                  <span className={`text-sm px-3 py-1 rounded-full ${elementStatus.bg} ${elementStatus.color}`}>
                    {elementStatus.text}
                  </span>
                </div>
              </div>
              <div className="mt-6">
                <Progress
                  percent={elementRateValue}
                  showInfo={false}
                  strokeColor="#52c41a"
                  trailColor="#e5e7eb"
                  strokeWidth={10}
                  style={{ marginBottom: 0 }}
                />
              </div>
            </div>
            <div className="ml-8">
              <RingChart
                percent={elementRateValue}
                size={140}
                strokeWidth={12}
                color={greenColor}
                bgColor="#e5e7eb"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染字段卡片网格
  const renderFieldCards = (fields) => {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
        {fields.map(([fieldName, rateStr]) => {
          const rate = parsePercentage(rateStr);
          const color = getColorByRate(rate);
          const status = getStatusLabel(rate);

          return (
            <div
              key={fieldName}
              className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
            >
              <div className="flex items-center gap-2 mb-3">
                {rate >= 90 ? (
                  <CheckCircleOutlined style={{ color, fontSize: '16px' }} />
                ) : (
                  <WarningOutlined style={{ color, fontSize: '16px' }} />
                )}
                <span className="text-gray-700 font-medium text-sm truncate" title={fieldName}>
                  {fieldName}
                </span>
              </div>
              <div
                className="text-3xl font-bold mb-2"
                style={{ color }}
              >
                {rateStr}
              </div>
              <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${rate}%`,
                    background: getGradientByRate(rate)
                  }}
                />
              </div>
              <div className={`mt-2 text-xs ${status.color}`}>
                {status.text}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 文件类型图标颜色数组（使用渐变蓝色和渐变绿色为主色系）
  const fileTypeColors = [
    { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#4facfe' },
    { bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#11998e' },
    { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#667eea' },
    { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#f093fb' },
    { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#4facfe' },
    { bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#11998e' },
  ];

  // 渲染文件类型卡片 - 使用渐变色背景
  const renderFileTypeCard = (fileTypeName, fileTypeData, index) => {
    const extractionRate = fileTypeData['当前文件类型的抽取准确率'];
    const elementRate = fileTypeData['当前文件类型的要素准确率'];
    const caseCount = fileTypeData['当前文件类型的用例总数']; // 提取用例总数
    const extractionRateValue = parsePercentage(extractionRate);
    const elementRateValue = parsePercentage(elementRate);
    const isExpanded = expandedTypes[fileTypeName];
    const colorScheme = fileTypeColors[index % fileTypeColors.length];

    // 获取字段列表（排除整体指标和用例总数，用例总数在卡片头部展示）
    const fields = Object.entries(fileTypeData).filter(([key]) =>
      key !== '当前文件类型的抽取准确率' &&
      key !== '当前文件类型的要素准确率' &&
      key !== '当前文件类型的用例总数'
    );

    // 准备柱状图数据
    const barChartData = fields.map(([name, rateStr]) => ({
      name,
      value: parsePercentage(rateStr)
    }));

    return (
      <div
        key={fileTypeName}
        className="mb-4 rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF0E5 14.28%, #FFFBE5 28.56%, #F0FFE5 42.84%, #E5F5FF 57.12%, #EBE5FF 71.4%, #F5E5FF 85.68%, #FFE5F5 100%)',
          border: 'none'
        }}
      >
        {/* 卡片头部 - 可点击展开 */}
        <div
          className="p-5 cursor-pointer transition-all duration-300"
          style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(10px)'
          }}
          onClick={() => toggleExpand(fileTypeName)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: colorScheme.bg }}
              >
                <FolderOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-800">{fileTypeName}</div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>{fields.length} 个字段</span>
                  {caseCount && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      用例数：{caseCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">文件类型抽取准确率</div>
                <div
                  className="text-2xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {extractionRate}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">要素准确率</div>
                <div
                  className="text-2xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {elementRate}
                </div>
              </div>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300"
                style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <DownOutlined style={{ color: '#fff', fontSize: '14px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* 展开内容 */}
        <div
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{
            maxHeight: isExpanded ? '1000px' : '0',
            opacity: isExpanded ? 1 : 0
          }}
        >
          <div className="p-6 bg-gray-50">
            {/* 文件类型整体指标 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600 text-sm">当前文件类型的抽取准确率</span>
                  <span
                    className="font-bold text-xl"
                    style={{ color: getColorByRate(extractionRateValue) }}
                  >
                    {extractionRate}
                  </span>
                </div>
                <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${extractionRateValue}%`,
                      background: getGradientByRate(extractionRateValue)
                    }}
                  />
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600 text-sm">当前文件类型的要素准确率</span>
                  <span
                    className="font-bold text-xl"
                    style={{ color: getColorByRate(elementRateValue) }}
                  >
                    {elementRate}
                  </span>
                </div>
                <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${elementRateValue}%`,
                      background: getGradientByRate(elementRateValue)
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 柱状图 */}
            {fields.length > 0 && (
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
                <div className="text-sm font-medium text-gray-700 mb-4">字段准确率分布</div>
                <BarChart data={barChartData} getColor={getColorByRate} />
              </div>
            )}

            {/* 字段卡片网格 */}
            {fields.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-3">字段准确率详情</div>
                {renderFieldCards(fields)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="result-stats-display">
      {title && (
        <h2 className="text-xl font-bold text-gray-800 mb-6">{title}</h2>
      )}

      {/* 整体指标（带环形图） */}
      {renderOverallMetrics()}

      {/* 文件类型列表 */}
      {fileTypes.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
            >
              <FolderOutlined style={{ fontSize: '16px', color: '#fff' }} />
            </div>
            <h3 className="text-lg font-semibold m-0" style={{
              background: 'linear-gradient(135deg, #FF9999, #FFC299, #FFED99, #C2FF99, #99D6FF, #B899FF, #D699FF, #FF99D6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              文件类型详情 ({fileTypes.length} 个类型)
            </h3>
          </div>
          {fileTypes.map(([fileTypeName, fileTypeData], index) =>
            renderFileTypeCard(fileTypeName, fileTypeData, index)
          )}
        </div>
      )}
    </div>
  );
}

export default ResultStatsDisplay;
